import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { MusicNoteIcon } from '../ValueProps/MusicNoteIcon';
import { Envelope, Eye, Fonts, Invitation, Link, MailingAddress, Mobile, Palette, Share, Website } from '@withjoy/joykit/icons';

export type DigitalValuePropItem = Readonly<{
  key: string;
  icon: JSX.Element;
  title: string;
  description: string;
}>;

export type DigitalInlineValueProp = Readonly<{
  key: string;
  icon: JSX.Element;
  description: string;
}>;

export type DigitalValuePropSection = Readonly<{
  key: 'included' | 'premiumAdditions' | 'purchaseOptions';
  title: string;
  items: ReadonlyArray<DigitalValuePropItem>;
}>;

const icon = (Icon: typeof Website) => (
  <Icon
    size={24}
    alignSelf="flex-start"
  />
);

export type DigitalValueProps = Readonly<{
  inline: ReadonlyArray<DigitalInlineValueProp>;
  sections: ReadonlyArray<DigitalValuePropSection>;
}>;

/**
 * The value props shared by the Premium Digital and Basic Digital V2 PDPs. Both tabs
 * render the same list so the tiers read as one comparison. The copy is still
 * settling, so every bullet lives here -- edit these arrays, not the panes.
 *
 * `inline` is the icon-over-title row mirroring the paper PDP's; its props are not
 * repeated in `sections`.
 */
export const useDigitalValuePropSections = (): DigitalValueProps => {
  const { tq } = useTranslation('stationery');

  return useMemo(
    () => ({
      inline: [
        { key: 'websiteDesign', icon: <Website />, description: tq`Matching Website Designs` },
        { key: 'contactCollector', icon: <MailingAddress />, description: tq`Contact Collector` },
        { key: 'shareable', icon: <Share />, description: tq`Easily Shareable` },
      ],
      sections: [
        {
          key: 'included',
          title: tq`Digital Cards include:`,
          items: [
            {
              key: 'links',
              icon: icon(Link),
              title: tq`Links`,
              description: tq`Link to your wedding website, RSVP, Add to Calendar, Registry, and more.`,
            },
            {
              key: 'tracking',
              icon: icon(Eye),
              title: tq`Tracking`,
              description: tq`Easily track which guests have received and opened your digital card.`,
            },
          ],
        },
        {
          key: 'premiumAdditions',
          title: tq`Premium Digital Cards additionally include:`,
          items: [
            {
              key: 'premiumCustomization',
              icon: icon(Fonts),
              title: tq`Premium Customization`,
              description: tq`Change fonts, move text around, upload your own image, and more.`,
            },
            {
              key: 'backgroundMusic',
              icon: <MusicNoteIcon />,
              title: tq`Background Music & Audio`,
              description: tq`Set the perfect mood for your guests with curated background music played right when they open your digital card.`,
            },
            {
              key: 'cardBack',
              icon: icon(Palette),
              title: tq`Card Back Design`,
              description: tq`Choose from a variety of card back designs or upload your own. Color match the front of your card.`,
            },
            {
              key: 'premiumEnvelope',
              icon: icon(Envelope),
              title: tq`Premium Envelope`,
              description: tq`Choose envelope colors and animations. Color match the front of your card.`,
            },
          ],
        },
        {
          key: 'purchaseOptions',
          title: tq`Options for purchase:`,
          items: [
            {
              key: 'textMessage',
              icon: icon(Mobile),
              title: tq`Text Message Delivery`,
              description: tq`Text messaging to USA and Canada with recipient tracking.`,
            },
            {
              key: 'matchingPaper',
              icon: icon(Invitation),
              title: tq`Matching Paper Cards`,
              description: tq`Order printed cards for special guests or keepsakes.`,
            },
          ],
        },
      ],
    }),
    [tq],
  );
};
